import { useCopilotAction } from "@copilotkit/react-core";
import { useAppStore } from "@hooks/useAppStore";
import { resendAccessCode } from "api/commerce/access-code-service";
import { events, useRedeemCopilotAnalytics } from "../analytics";
import { COPILOT_ACTIONS } from "../constants/actions";
import { getCopilotAction } from "../helpers";
import { COPILOT_CHOICES } from "../instructions/choices";
import type { ActionExecutionResult } from "../renderers";

export const useRcResendCodes = () => {
	const tenantId = useAppStore((store) => store.tenant.id);

	const { trackRedeemCopilotEvent } = useRedeemCopilotAnalytics();

	useCopilotAction({
		...getCopilotAction(COPILOT_ACTIONS.RESEND_ACCESS_CODES),
		parameters: [
			{
				name: "orderId",
				type: "string",
				description: "The order ID to resend the codes for",
			},
		],
		handler: async ({
			orderId,
		}: { orderId: string }): Promise<ActionExecutionResult> => {
			try {
				const { success, message } = await resendAccessCode({
					orderId,
					tenantId,
				});

				if (success) {
					const type = "success";
					const msg = message || "Codes resent successfully";

					trackRedeemCopilotEvent(events.resendCodesSucceeded, {
						type,
						message: msg,
					});

					return {
						type,
						message: msg,
						instructions: COPILOT_CHOICES.RESEND_CODES_SUCCESS,
					};
				}

				const type = "error";
				const msg = message || "Failed to resend codes";

				trackRedeemCopilotEvent(events.resendCodesFailed, {
					type,
					message: msg,
				});

				return {
					type,
					message: msg,
					instructions: COPILOT_CHOICES.RESEND_CODES_ERROR,
				};
			} catch (e) {
				const type = "error";
				const msg = e instanceof Error ? e.message : String(e);

				trackRedeemCopilotEvent(events.resendCodesFailed, {
					type,
					message: msg,
				});

				return {
					type,
					message: msg,
					instructions: COPILOT_CHOICES.RESEND_CODES_ERROR,
				};
			}
		},
	});
};
